import React, { useState, useEffect, useContext } from "react";
import { Layout, Menu } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  DashboardFilled,
  PayCircleFilled,
  DollarOutlined,
  AuditOutlined,
  FileTextOutlined,
  SettingFilled,
  BankOutlined,
} from "@ant-design/icons";
import { FaGasPump } from "react-icons/fa";
import logo from "../../Assets/Images/logo.svg";
import logout from "../../Assets/Images/logout.svg";
import { UserContext } from "../../App";
import {
  handleLogout,
  getRefreshToken,
} from "../../Utlis/UpdateUserState";
import { logout as logoutApi } from "../../API/auth";
import { getFuelVendorProfile } from "../../API/FuelVendor/MyProfile";
import useProfileSync from "../../Utlis/useProfileSync";

import "./style.css";
import { GrUserSettings } from "react-icons/gr";
import InitialsAvatar from "../InitialsAvatar";

const { Sider } = Layout;

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const SidebarVendor = ({ collapsed, setCollapsed, setUser }) => {
  const { userData, setUserToken } = useContext(UserContext);
  const location = useLocation();
  const [selectedId, setSelectedId] = useState("1");
  const navigate = useNavigate();
  const pageAccess = userData?.page_access || {};
  const [profileInfo, setProfileInfo] = useState({ name: "", role: "", avatar: null });

  useProfileSync(getFuelVendorProfile, (data) => {
    setProfileInfo({
      name: data.name || "",
      role: data.role || "",
      avatar: resolveAvatarUrl(data.avatar),
    });
  });

  useEffect(() => {
    // Reflect avatar changes made on the Profile page instantly, without
    // waiting for a remount/refetch of this sidebar.
    if (!userData?.avatar) return;

    setProfileInfo((prev) => ({
      ...prev,
      avatar: resolveAvatarUrl(userData.avatar),
    }));
  }, [userData?.avatar]);

  useEffect(() => {
    if (!userData?.name && !userData?.role) return;

    setProfileInfo((prev) => ({
      ...prev,
      name: userData.name || prev.name,
      role: userData.role || prev.role,
    }));
  }, [userData?.name, userData?.role]);

  const pageAccessSlugMap = {
    "1": "dashboard",
    "2": "transactions",
    "3": "settlement",
    "4": "fuel-pricing",
    "5": "fuel-dispensed",
    "6": "audit-trail",
    "7": "staff-management",
    "8": "settings-permissions",
    "9": "vendor-details",
  };

  const isPageVisible = (key) => pageAccess[pageAccessSlugMap[key]] !== "restrict";

  const routeKeyMap = {
    "/dashboard-vendor": "1",
    "/transactions-vendor": "2",
    "/settlement": "3",
    "/fuel-pricing": "4",
    "/fuel-dispensed": "5",
    "/audit-trail-vendor": "6",
    "/staff-management": "7",
    "/settings-permissions-vendor": "8",
    "/vendor-details": "9",
  };

  useEffect(() => {
    const matched = Object.keys(routeKeyMap).find((r) =>
      location.pathname.startsWith(r),
    );
    if (matched) {
      setSelectedId(routeKeyMap[matched]);
    } else if (location.pathname.startsWith("/profile-vendor")) {
      // Profile isn't a menu page, so nothing should appear selected
      setSelectedId("");
    }
  }, [location.pathname]);

  const items = [
    {
      type: "group",
      label: "All Pages",
      children: [
        {
          key: "1",
          icon: <DashboardFilled />,
          label: <Link to="/dashboard-vendor">Dashboard</Link>,
        },
        {
          key: "2",
          icon: <PayCircleFilled />,
          label: <Link to="/transactions-vendor">Transactions</Link>,
        },
        {
          key: "3",
          icon: <FileTextOutlined />,
          label: <Link to="/settlement">Settlement</Link>,
        },
        {
          key: "4",
          icon: <DollarOutlined />,
          label: <Link to="/fuel-pricing">Fuel Pricing</Link>,
        },
        {
          key: "5",
          icon: <FaGasPump />,
          label: <Link to="/fuel-dispensed">Fuel Dispensed</Link>,
        },
        {
          key: "7",
          icon: <GrUserSettings />,
          label: <Link to="/staff-management">Staff Management</Link>,
        },
        {
          key: "6",
          icon: <AuditOutlined />,
          label: <Link to="/audit-trail-vendor">Audit Trail</Link>,
        },
        {
          key: "8",
          icon: <SettingFilled />,
          label: (
            <Link to="/settings-permissions-vendor">
              Settings & Permissions
            </Link>
          ),
        },
        {
          key: "9",
          icon: <BankOutlined />,
          label: <Link to="/vendor-details">Vendor Detail</Link>,
        },
      ].filter((item) => isPageVisible(item.key)),
    },
  ];

  const handleProfileClick = () => {
    navigate("/profile-vendor");
  };

  return (
    <Sider
      className="sidebar"
      width={240}
      collapsible
      collapsed={collapsed}
      onCollapse={(val) => setCollapsed(val)}
      collapsedWidth={80}
      breakpoint="lg"
      onBreakpoint={(broken) => {
        setCollapsed(broken);
      }}
      theme="light"
      style={{ height: "100vh", position: "fixed" }}
    >
      <div className={`logo-container ${collapsed ? "collapsed" : ""}`}>
        <img src={logo} alt="logo" style={{ width: "143px" }} />
      </div>

      <div className="sidebar-divider"></div>

      <div
        style={{
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Menu
          mode="inline"
          theme="light"
          selectedKeys={[selectedId]}
          items={items}
        />

        <div className={`logout-div ${collapsed ? "collapsed" : ""}`}>
          <div className="logout-user" onClick={handleProfileClick}>
            <InitialsAvatar
              className="profile-pic"
              src={profileInfo.avatar}
              name={profileInfo.name}
              alt={profileInfo.name || "Profile"}
              style={{ fontSize: 14 }}
            />

            <div className="logout-user-details">
              <div className="name">{profileInfo.name || "-"}</div>
              <div className="role">{profileInfo.role || "-"}</div>
            </div>

            <div className="logout-icon">
              <img src={logout} alt="logout Icon" />
            </div>
          </div>
        </div>
      </div>
    </Sider>
  );
};

export default SidebarVendor;
